import { WebhookDispatch } from '@/@types/WebhookDispatch';
import Ticket from '@/assets/icons/Ticket';
import { usePermissions } from '@/auth/PermissionsContext';
import { IntegrationsTabsSection } from '@/components/Integrations/integrations-tab-section';
import Badge from '@/components/shared/Badge/Badge';
import { MultiComboBox } from '@/components/shared/MultiComboBox/MultiComboBox';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import WebhookDetailsModal, { WEBHOOK_DETAILS_MODAL } from '@/components/shared/WebhookDetailsModal/WebhookDetailsModal';
import { Card } from '@/components/ui/card';
import { Command, CommandInput } from '@/components/ui/command';
import { TableCell, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { IntegrationsService } from '@/services/integrations.service';
import useModalStore from '@/store/useModalStore';
import { formatDateForAPI, getDefaultDate } from '@/utils/dateHelpers';
import { getIntegrationsForWebhookFilter, getIntegrationWithIcon } from '@/utils/integrations';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, Loader2, RotateCcw } from 'lucide-react';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsIsoDateTime,
  parseAsString,
  useQueryState,
} from 'nuqs';
import { motion } from 'framer-motion';
import { useCallback, useMemo, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import DateRangePicker from '../Dashboard/Partials/DateRangePicker';

export default function WebhooksHistory() {
  const { t } = useTranslation(['integration', 'common']);

  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { openModal } = useModalStore();

  const { hasPermission } = usePermissions();
  const canResendWebhook = hasPermission('integrations.resend_webhook');

  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookDispatch | null>(null);

  const [fromDate, setFromDate] = useQueryState(
    'from',
    parseAsIsoDateTime.withDefault(getDefaultDate())
  );
  const [toDate, setToDate] = useQueryState(
    'to',
    parseAsIsoDateTime.withDefault(getDefaultDate())
  );

  const [statusFilter, setStatusFilter] = useQueryState(
    'status',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  const [integration, setIntegration] = useQueryState(
    'integration',
    parseAsArrayOf(parseAsString).withDefault([])
  );

  const [search, setSearch] = useQueryState(
    'search',
    parseAsString.withDefault('')
  );

  const [currentPage, setCurrentPage] = useQueryState(
    'page',
    parseAsInteger.withDefault(1)
  );

  const [debouncedSearch] = useDebounce(search, 450);

  const dateRange = useMemo(
    (): DateRange => ({
      from: fromDate,
      to: toDate,
    }),
    [fromDate, toDate]
  );

  const { data: webhooksHistory, isPending: isLoadingWebhookHistory } =
    useQuery({
      queryKey: [
        `webhooks-history`,
        currentPage,
        fromDate,
        toDate,
        statusFilter,
        integration,
        debouncedSearch,
      ],
      queryFn: () =>
        IntegrationsService.getWebhooksHistory({
          page: currentPage,
          startDate: formatDateForAPI(fromDate) || '',
          endDate: formatDateForAPI(toDate) || '',
          q: debouncedSearch,
          status: statusFilter.length ? statusFilter.join(',') : undefined,
          integrations: integration.length ? integration.join(',') : undefined,
        }),
    });

  const headers = [
    { title: t('order_id'), key: 'order_id' },
    { title: t('event'), key: 'event_type' },
    { title: t('name'), key: 'name' },
    { title: t('integration'), key: 'integration' },
    { title: t('send_status'), key: 'send_status' },
    { title: t('send_date'), key: 'creation_date' },
    { title: t('actions'), key: 'actions' },
  ];

  const handleDateRangeChange = useCallback(
    (range: DateRange | undefined) => {
      if (range?.from) {
        setFromDate(range.from);
      }
      if (range?.to) {
        setToDate(range.to);
      }
    },
    [setFromDate, setToDate]
  );

  const status = [
    { id: 2, label: t('common:success'), value: 'success' },
    { id: 3, label: t('common:failed'), value: 'failed' },
    { id: 4, label: t('common:pending'), value: 'pending' },
    { id: 5, label: t('common:processing'), value: 'processing' },
    { id: 6, label: t('common:retrying'), value: 'retrying' },
  ];

  const integrationTypes = getIntegrationsForWebhookFilter();

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (id: string) => IntegrationsService.retrySendingWebhook(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['webhooks-history'],
        exact: false,
      });
      toast({
        description: t('retry_success'),
        variant: 'success',
      });
    },
    onError: (error: any) => {
      toast({
        description: error?.message || t('common:data_submission_error'),
        variant: 'destructive',
      });
    },
  });

  const handleRetry = async (id: string) => {
    setRetryingId(id);
    mutate(id, {
      onSettled: () => setRetryingId(null),
    });
  };

  const handleViewDetails = (webhook: WebhookDispatch) => {
    setSelectedWebhook(webhook);
    openModal(WEBHOOK_DETAILS_MODAL);
  };

  const fetchStatusOptions = async (query: string) => {
    return status.filter((status) =>
      status.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  const fetchIntegrationOptions = async (query: string) => {
    return integrationTypes.filter((integration) =>
      integration.label.toLowerCase().includes(query.toLowerCase())
    );
  };

  return (
    <IntegrationsTabsSection>
      <motion.section
        className='mb-6'
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className='text-xl font-semibold'>{t('webhooks_history')}</h2>
        <p className='text-muted text-sm mt-2'>
          {t('webhooks_description')}
        </p>
      </motion.section>
      <motion.div
        className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <DateRangePicker
          defaultValue={dateRange}
          onChange={handleDateRangeChange}
          className='!w-full'
          widthFull
        />
        <Command>
          <CommandInput
            placeholder={t('search_by_id')}
            value={search}
            onValueChange={handleSearchChange}
            className='w-full md:w-[336px]'
            data-cy='search-input'
          />
        </Command>

        <MultiComboBox
          placeholder={t('send_status')}
          fetchOptions={fetchStatusOptions}
          values={statusFilter}
          onChange={(values) => {
            setStatusFilter(values);
            setCurrentPage(1);
          }}
          key={'status-multicombobox'}
          queryKey='statusTypes'
        />

        <MultiComboBox
          values={integration}
          fetchOptions={fetchIntegrationOptions}
          placeholder={t('integrations')}
          onChange={(values) => {
            setCurrentPage(1);
            setIntegration(values);
          }}
          key={'integration-multicombobox'}
          queryKey='integrationTypes'
        />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
      <Card>
        <TableListCustom
          emptyDescriptionMessage={t('empty_history.description')}
          emptyIconMessage={<Ticket />}
          emptyTitleMessage={t('empty_history.title')}
          headers={headers}
          isLoadingData={isLoadingWebhookHistory}
          data={webhooksHistory}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          key={'webhooks-history-table'}
        >
          {webhooksHistory?.data?.map((webhooks: WebhookDispatch) => (
            <TableRow
              key={webhooks?.id}
              className='h-[64px] border-b border-b-colorBorder hover:bg-transparent'
              data-cy='table-row'
            >
              <TableCell
                className='font-medium'
                onClick={() =>
                  navigate(`/sales/sales-details/${webhooks?.order?.id}`)
                }
                style={{ cursor: 'pointer' }}
              >
                {webhooks?.order?.id}
              </TableCell>

              <TableCell className='font-medium'>{webhooks?.event}</TableCell>

              <TableCell className='font-medium'>
                {webhooks?.integration?.name}
              </TableCell>
              <TableCell>
                <div>
                  {getIntegrationWithIcon(
                    webhooks?.integration?.integration
                  )}
                </div>
              </TableCell>
              <TableCell>
                {' '}
                <Badge status={webhooks?.status} />
              </TableCell>
              <TableCell>{webhooks?.sent_at_formatted ?? '------'}</TableCell>
              <TableCell className='w-auto'>
                {retryingId === webhooks?.id ? (
                  <span className='flex items-center gap-2'>
                    <Loader2 className='animate-spin text-primary' />
                  </span>
                ) : (
                  <CTATable
                    items={[
                      {
                        id: 1,
                        label: t('view_details'),
                        icon: <Eye />,
                        action: () => handleViewDetails(webhooks),
                      },
                      ...(canResendWebhook ? [{
                        id: 2,
                        label: t('retry'),
                        icon: <RotateCcw />,
                        action: () => handleRetry(webhooks?.id),
                        disabled: webhooks?.status !== 'failed',
                      }] : []),
                    ]}
                  />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableListCustom>
      </Card>
      </motion.div>

      <WebhookDetailsModal
        webhook={selectedWebhook}
        onRetrySuccess={() => setSelectedWebhook(null)}
      />
    </IntegrationsTabsSection>
  );
}
